import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ApplyLeaveTab({ onLeaveApplied, editingLeaveId }) {
  const [formData, setFormData] = useState({
    leave_type: "casual",
    start_date: "",
    end_date: "",
    duration: "",
    reason: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const token = localStorage.getItem("token");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (update) => {
    setDateRange(update);
    const [start, end] = update;

    // Helper to format date in local timezone (not UTC)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (start) {
      setFormData((prev) => ({
        ...prev,
        start_date: formatLocalDate(start),
      }));
    }

    if (end) {
      let count = 0;
      const current = new Date(start);

      while (current <= end) {
        const day = current.getDay();
        const isSunday = day === 0;
        const isAltSaturday = isAlternateSaturday(new Date(current));

        if (!isSunday && !isAltSaturday) {
          count++;
        }

        current.setDate(current.getDate() + 1);
      }

      setFormData((prev) => ({
        ...prev,
        end_date: formatLocalDate(end),
        duration: count,
      }));
    }

  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
      duration: parseFloat(formData.duration),
    };

    // If editing → add leave_id
    if (editingLeaveId) {
      payload.leave_id = editingLeaveId;
    }

    const url = editingLeaveId
      ? `${API}/leaves/update` // update API
      : `${API}/leaves/apply`; // apply API

    try {
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        toast.success(
          editingLeaveId
            ? "Leave updated successfully"
            : "Leave application submitted successfully"
        );

        // reset only after apply, not after update (optional)
        setFormData({
          leave_type: "casual",
          start_date: "",
          end_date: "",
          duration: "",
          reason: "",
        });
        setDateRange([null, null]);

        onLeaveApplied();
      } else {
        toast.error(response.data.message || "Failed to submit leave");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to submit leave");
    } finally {
      setIsSubmitting(false);
    }
  };

  const OFF_SATURDAY_REF = new Date(2025, 0, 11); // Jan = 0

  const isAlternateSaturday = (date) => {
    if (date.getDay() !== 6) return false; // not Saturday

    const diffDays = Math.floor(
      (date.setHours(0, 0, 0, 0) - OFF_SATURDAY_REF.getTime()) /
        (1000 * 60 * 60 * 24),
    );

    return diffDays % 14 === 0;
  };



  return (
    <div
      data-testid="apply-leave-tab"
      style={{
        display: "flex",
        width: "100%",
        gap: "2rem",
        alignItems: "flex-start",
        justifyContent: "space-evenly",
        flexWrap: "wrap",
        padding: "2rem",
        backgroundColor: "#f8f9fa",
        borderRadius: "16px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
      }}
    >
      {/* Left: Form */}
      <div
        style={{
          flex: 1,
          minWidth: "350px",
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "1.5rem 2rem",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            marginBottom: "1.5rem",
            color: "#2d3748",
          }}
        >
          Apply for Leave
        </h2>

        <form onSubmit={handleSubmit} data-testid="apply-leave-form">
          <div className="form-field">
            <label htmlFor="leave_type">Leave Type</label>
            <select
              id="leave_type"
              name="leave_type"
              data-testid="leave-type-select"
              value={formData.leave_type}
              onChange={handleChange}
              required
            >
              <option value="casual">Casual Leave</option>
              <option value="sick">Sick Leave</option>
            </select>
          </div>

          <div className="form-row" style={{ display: "flex", gap: "1rem" }}>
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="start_date">Start Date</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                data-testid="start-date-input"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="end_date">End Date</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                data-testid="end-date-input"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="duration">Duration (days)</label>
            <input
              type="number"
              id="duration"
              name="duration"
              data-testid="duration-input"
              value={formData.duration}
              onChange={handleChange}
              placeholder="e.g., 1 or 0.5 for half day"
              step="0.5"
              min="0.5"
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="reason">Reason</label>
            <textarea
              id="reason"
              name="reason"
              data-testid="reason-input"
              value={formData.reason}
              onChange={handleChange}
              placeholder="Please provide a reason for your leave"
              required
            />
          </div>

          <button
            type="submit"
            className="submit-btn"
            data-testid="submit-leave-btn"
            disabled={isSubmitting}
            style={{
              marginTop: "1rem",
              width: "100%",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "0.75rem",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            {isSubmitting
              ? editingLeaveId
                ? "Updating..."
                : "Submitting..."
              : editingLeaveId
                ? "Update Leave"
                : "Submit Leave Application"}
          </button>
        </form>
      </div>

      {/* Right: Calendar */}
      <div
        style={{
          flex: 1,
          minWidth: "420px",
          minHeight: "630px",
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "2rem",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            marginBottom: "1rem",
            color: "#2d3748",
          }}
        >
          Select Dates
        </h2>

        {/* Calendar */}
        <div
          style={{
            transform: "scale(1.4)",
            transformOrigin: "top left",
            marginBottom: "8rem", // ⬅️ increased to push legend below scaled area
          }}
        >
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            inline
            filterDate={(date) => {
              const isSunday = date.getDay() === 0;
              const isAltSaturday = isAlternateSaturday(date);
              return !(isSunday || isAltSaturday);
            }}
            dayClassName={(date) => {
              const isSunday = date.getDay() === 0;
              const isAltSaturday = isAlternateSaturday(date);
              if (isSunday || isAltSaturday) return "disabled-day";
              return "";
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default ApplyLeaveTab;
